// src/odoo/transactions_dashboard/hooks/useTransactionsDashboard.ts
// All comments are in English.

import { useQuery } from "@tanstack/react-query";
import {
  fetchConfirmedOrdersTrend,
  fetchConfirmedVsInvoicedTrend,
  fetchTopCustomers,
  fetchProductRankings,
  fetchIntraBrandShare,
  fetchMonthlyProfit,
  fetchUninvoicedTrend,
  fetchOpenQuotationsMonthDetail,
  fetchUninvoicedMonthDetail,
  fetchInvoicePaymentStatusTrend,
  fetchOverdueInvoicesMonthDetail,
} from "../api/transactionsDashboardApi";
import type {
  TransactionsDashboardFilters,
  ConfirmedOrdersTrendPoint,
  ConfirmedVsInvoicedTrendPoint,
  TopCustomerItem,
  ProductRankingsResponse,
  IntraBrandShareResponse,
  MonthlyProfitPoint,
  UninvoicedTrendPoint,
  ConfirmedOrdersMonthDetail,
  UninvoicedMonthDetail,
  InvoicePaymentStatusTrendPoint,
  OverdueInvoiceMonthDetail,
} from "../api/transactionsDashboardApi";

export function useConfirmedOrdersTrend(filters: TransactionsDashboardFilters) {
  return useQuery<ConfirmedOrdersTrendPoint[], Error>({
    queryKey: ["transactions-dashboard", "confirmed-orders-trend", filters],
    queryFn: () => fetchConfirmedOrdersTrend(filters),
    keepPreviousData: true,
  });
}

export function useConfirmedVsInvoicedTrend(filters: TransactionsDashboardFilters) {
  return useQuery<ConfirmedVsInvoicedTrendPoint[], Error>({
    queryKey: ["transactions-dashboard", "confirmed-vs-invoiced-trend", filters],
    queryFn: () => fetchConfirmedVsInvoicedTrend(filters),
    keepPreviousData: true,
  });
}

export function useTopCustomers(filters: TransactionsDashboardFilters) {
  return useQuery<TopCustomerItem[], Error>({
    queryKey: ["transactions-dashboard", "top-customers", filters],
    queryFn: () => fetchTopCustomers(filters),
    keepPreviousData: true,
  });
}

export function useProductRankings(filters: TransactionsDashboardFilters) {
  return useQuery<ProductRankingsResponse, Error>({
    queryKey: ["transactions-dashboard", "product-rankings", filters],
    queryFn: () => fetchProductRankings(filters),
    keepPreviousData: true,
  });
}

export function useIntraBrandShare(filters: TransactionsDashboardFilters) {
  return useQuery<IntraBrandShareResponse, Error>({
    queryKey: ["transactions-dashboard", "intra-brand-share", filters],
    queryFn: () => fetchIntraBrandShare(filters),
    keepPreviousData: true,
  });
}

export function useMonthlyProfit(filters: TransactionsDashboardFilters) {
  return useQuery<MonthlyProfitPoint[], Error>({
    queryKey: ["transactions-dashboard", "monthly-profit", filters],
    queryFn: () => fetchMonthlyProfit(filters),
    keepPreviousData: true,
  });
}

export function useUninvoicedTrend(filters: TransactionsDashboardFilters) {
  return useQuery<UninvoicedTrendPoint[], Error>({
    queryKey: ["transactions-dashboard", "uninvoiced-trend", filters],
    queryFn: () => fetchUninvoicedTrend(filters),
    keepPreviousData: true,
  });
}

// -----------------------------------------------------------------------------
// Month detail: open quotations created in a given month.
// -----------------------------------------------------------------------------

export function useOpenQuotationsMonthDetail(params: {
  monthStart?: string | null;
  companyId?: number;
}) {
  const enabled = !!params.monthStart;

  return useQuery<ConfirmedOrdersMonthDetail, Error>({
    queryKey: ["transactions-dashboard", "open-quotations-month-detail", params],
    queryFn: () => {
      if (!params.monthStart) {
        throw new Error("monthStart is required");
      }
      return fetchOpenQuotationsMonthDetail({
        monthStart: params.monthStart,
        companyId: params.companyId,
      });
    },
    enabled,
    keepPreviousData: true,
  });
}

export function useUninvoicedMonthDetail(params: {
  monthStart?: string | null;
  companyId?: number;
}) {
  const enabled = !!params.monthStart;

  return useQuery<UninvoicedMonthDetail, Error>({
    queryKey: ["transactions-dashboard", "uninvoiced-month-detail", params],
    queryFn: () => {
      if (!params.monthStart) {
        throw new Error("monthStart is required");
      }
      return fetchUninvoicedMonthDetail({
        monthStart: params.monthStart,
        companyId: params.companyId,
      });
    },
    enabled,
    keepPreviousData: true,
  });
}

export function useInvoicePaymentStatusTrend(
  filters: TransactionsDashboardFilters
) {
  return useQuery<InvoicePaymentStatusTrendPoint[], Error>({
    queryKey: ["transactions-dashboard", "invoice-payment-status-trend", filters],
    queryFn: () => fetchInvoicePaymentStatusTrend(filters),
    keepPreviousData: true,
  });
}

export function useOverdueInvoicesMonthDetail(params: {
  monthStart?: string | null;
  companyId?: number;
}) {
  const enabled = !!params.monthStart;

  return useQuery<OverdueInvoiceMonthDetail, Error>({
    queryKey: ["transactions-dashboard", "overdue-invoices-month-detail", params],
    queryFn: () => {
      if (!params.monthStart) {
        throw new Error("monthStart is required");
      }
      return fetchOverdueInvoicesMonthDetail({
        monthStart: params.monthStart,
        companyId: params.companyId,
      });
    },
    enabled,
    keepPreviousData: true,
  });
}


/**
 * Backward-compatible alias. Old code that still imports
 * useConfirmedOrdersMonthDetail will keep working.
 */
export const useConfirmedOrdersMonthDetail = useOpenQuotationsMonthDetail;
